// ============================================================================
// ROLE ADMIN SERVICE — Settings → Roles & Permissions. Roles and their rights
// are configured by an authorised user; the seeds only give a fresh install its
// defaults. FBP_SUPERADMIN is read-only.
// ============================================================================

import { del, get, post, put } from '../client/axiosClient';
import { roleAdminEndpoints } from '../endpoints/iam.endpoints';
import type {
  ApiResponse, PermissionCatalogDto, RoleCreateRequest, RolePermissionsDto, RoleSummaryDto, RoleUpdateRequest,
  SaveRolePermissionsResult,
} from '../types';

class RoleAdminService {
  async getRoles(): Promise<RoleSummaryDto[]> {
    const r = await get<ApiResponse<RoleSummaryDto[]>>(roleAdminEndpoints.roles());
    return r.data.data ?? [];
  }

  async getCatalog(): Promise<PermissionCatalogDto> {
    const r = await get<ApiResponse<PermissionCatalogDto>>(roleAdminEndpoints.catalog());
    return r.data.data;
  }

  async getRole(id: string): Promise<RolePermissionsDto> {
    const r = await get<ApiResponse<RolePermissionsDto>>(roleAdminEndpoints.role(id));
    return r.data.data;
  }

  async createRole(data: RoleCreateRequest): Promise<RolePermissionsDto> {
    const r = await post<ApiResponse<RolePermissionsDto>>(roleAdminEndpoints.roles(), data);
    return r.data.data;
  }

  async updateRole(id: string, data: RoleUpdateRequest): Promise<RolePermissionsDto> {
    const r = await put<ApiResponse<RolePermissionsDto>>(roleAdminEndpoints.role(id), data);
    return r.data.data;
  }

  /** Replaces the role's rights with exactly this set. */
  async saveRights(id: string, resourceActionIds: string[], rowVersion: number): Promise<SaveRolePermissionsResult> {
    const r = await put<ApiResponse<SaveRolePermissionsResult>>(roleAdminEndpoints.rights(id), { resourceActionIds, rowVersion });
    return r.data.data;
  }

  async deleteRole(id: string): Promise<void> {
    await del(roleAdminEndpoints.role(id));
  }
}

export const roleAdminService = new RoleAdminService();
